"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { memo, useCallback, useState } from "react";
import type { DropIdentityMode, DropInteractionParams } from "../drop.types";
import { DropLocation } from "../drop.types";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "../dropRankStyles";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropHeader from "../WaveDropHeader";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropRatings from "../WaveDropRatings";
import WaveDropReactions from "../WaveDropReactions";
import WaveDropReply from "../WaveDropReply";
import WinnerDropBadge from "./WinnerDropBadge";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
import { getWinnerVisibleMetadata } from "@/components/waves/winners/identity/winnerIdentity.helpers";
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
const getDropStyles = (isActiveDrop: boolean) => {
  if (isActiveDrop) {
    return {
      borderColor: "rgba(60,203,127,0.45)",
      boxShadow: "inset 0 0 0 1px rgba(60,203,127,0.2), inset 0 0 24px rgba(60,203,127,0.08)"
    };
  }
  return undefined;
};
interface DefautWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly showInteractions?: boolean | undefined;
}
const DefaultWinnerDrop = t0 => {
  const $ = _c(95);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    identityMode: t1,
    showInteractions: t2
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const showInteractions = t2 === undefined ? true : t2;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  const decisionTime = drop.winning_context?.decision_time;
  const showIdentity = identityMode !== "hidden";
  let t3;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t3 = getWinnerVisibleMetadata({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const visibleMetadata = t3;
  const getBackgroundColorClass = _temp;
  const bgColorClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getBackgroundColorClass(location);
  let t4;
  if ($[3] !== hasTouch || $[4] !== showInteractions) {
    t4 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[3] = hasTouch;
    $[4] = showInteractions;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleLongPress = t4;
  let t5;
  if ($[6] !== activePartIndex || $[7] !== drop || $[8] !== onReply) {
    t5 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[6] = activePartIndex;
    $[7] = drop;
    $[8] = onReply;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleOnReply = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsSlideUp(false);
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleOnAddReaction = t6;
  const t7 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  let t8;
  if ($[11] !== effectiveRank) {
    t8 = getRankStaticBorderClass(effectiveRank);
    $[11] = effectiveRank;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== effectiveRank) {
    t9 = getRankHoverClass(effectiveRank);
    $[13] = effectiveRank;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t8} tw-px-4 tw-py-3 ${bgColorClass} ${t9}`;
  let t11;
  if ($[15] !== isActiveDrop) {
    t11 = getDropStyles(isActiveDrop);
    $[15] = isActiveDrop;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11) {
    t12 = {
      ...t11,
      transition: "box-shadow 0.2s ease, background-color 0.2s ease"
    };
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== drop.reply_to || $[20] !== drop.wave || $[21] !== dropViewDropId || $[22] !== onReplyClick) {
    t13 = drop.reply_to && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[19] = drop.reply_to;
    $[20] = drop.wave;
    $[21] = dropViewDropId;
    $[22] = onReplyClick;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== drop || $[25] !== showIdentity) {
    t14 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[24] = drop;
    $[25] = showIdentity;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== activePartIndex || $[28] !== decisionTime || $[29] !== drop || $[30] !== effectiveRank || $[31] !== identityMode || $[32] !== isStorm || $[33] !== showIdentity) {
    t15 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} /> : <WaveDropHeader drop={drop} showWaveInfo={false} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} badge={<WinnerDropBadge rank={effectiveRank} decisionTime={decisionTime ?? null} />} />);
    $[27] = activePartIndex;
    $[28] = decisionTime;
    $[29] = drop;
    $[30] = effectiveRank;
    $[31] = identityMode;
    $[32] = isStorm;
    $[33] = showIdentity;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== drop.wave || $[36] !== identityMode || $[37] !== showWaveInfo) {
    t16 = identityMode === "default" && showWaveInfo && (() => {
      const waveDetails = drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      };
      const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={_temp2} className="tw-mt-0.5 tw-text-xs tw-leading-none tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[35] = drop.wave;
    $[36] = identityMode;
    $[37] = showWaveInfo;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t15 || $[40] !== t16) {
    t17 = <div className="tw-flex tw-flex-col tw-items-start">{t15}{t16}</div>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = showIdentity ? "tw-mt-2" : "";
  let t19;
  if ($[42] !== activePartIndex || $[43] !== drop || $[44] !== handleLongPress || $[45] !== hasTouch || $[46] !== onDropContentClick || $[47] !== onQuoteClick) {
    t19 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} onLongPress={handleLongPress} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} />;
    $[42] = activePartIndex;
    $[43] = drop;
    $[44] = handleLongPress;
    $[45] = hasTouch;
    $[46] = onDropContentClick;
    $[47] = onQuoteClick;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t18 || $[50] !== t19) {
    t20 = <div className={t18}>{t19}</div>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t17 || $[53] !== t20) {
    t21 = <div className="tw-flex tw-w-full tw-flex-col">{t17}{t20}</div>;
    $[52] = t17;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t14 || $[56] !== t21) {
    t22 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t14}{t21}</div>;
    $[55] = t14;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== activePartIndex || $[59] !== drop || $[60] !== handleOnReply || $[61] !== isMobile || $[62] !== showInteractions || $[63] !== showReplyAndQuote) {
    t23 = !isMobile && showInteractions && showReplyAndQuote && <div className="tw-absolute tw-right-0 tw-top-1"><WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} /></div>;
    $[58] = activePartIndex;
    $[59] = drop;
    $[60] = handleOnReply;
    $[61] = isMobile;
    $[62] = showInteractions;
    $[63] = showReplyAndQuote;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  const t24 = `${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-flex tw-flex-col tw-gap-2`;
  let t25;
  if ($[65] !== drop) {
    t25 = <WaveWinnerIdentity drop={drop} variant="full" cardVariant="chat" />;
    $[65] = drop;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== visibleMetadata) {
    t26 = visibleMetadata.length > 0 && <WaveDropMetadata metadata={visibleMetadata} />;
    $[67] = visibleMetadata;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== drop || $[70] !== showInteractions) {
    t27 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{!!drop.raters_count && <WaveDropRatings drop={drop} />}<WaveDropReactions drop={drop} /></div>;
    $[69] = drop;
    $[70] = showInteractions;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t24 || $[73] !== t25 || $[74] !== t26 || $[75] !== t27) {
    t28 = <div className={t24}>{t25}{t26}{t27}</div>;
    $[72] = t24;
    $[73] = t25;
    $[74] = t26;
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== t10 || $[78] !== t12 || $[79] !== t13 || $[80] !== t22 || $[81] !== t23 || $[82] !== t28) {
    t29 = <div className={t10} style={t12}>{t13}{t22}{t23}{t28}</div>;
    $[77] = t10;
    $[78] = t12;
    $[79] = t13;
    $[80] = t22;
    $[81] = t23;
    $[82] = t28;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== drop || $[85] !== handleOnReply || $[86] !== isSlideUp || $[87] !== longPressTriggered || $[88] !== showInteractions || $[89] !== showReplyAndQuote) {
    t30 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[84] = drop;
    $[85] = handleOnReply;
    $[86] = isSlideUp;
    $[87] = longPressTriggered;
    $[88] = showInteractions;
    $[89] = showReplyAndQuote;
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  let t31;
  if ($[91] !== t29 || $[92] !== t30 || $[93] !== t7) {
    t31 = <div className={t7}>{t29}{t30}</div>;
    $[91] = t29;
    $[92] = t30;
    $[93] = t7;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  return t31;
};
export default memo(DefaultWinnerDrop);
function _temp(_loc) {
  return "tw-bg-iron-950";
}
function _temp2(e) {
  return e.stopPropagation();
}